import React, { useEffect, useState } from 'react';
import { Typography, Switch, Box } from '@mui/material';
import {
  BadgeOutlined, AssignmentOutlined, RecordVoiceOverOutlined, StarOutline,
} from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard } from '../tabLayout';
import { ListRow, OnOffPill } from './sectionParts';
import { AssessmentPathway } from '../../../types/admissions';

interface Props {
  pathway: AssessmentPathway;
  canEdit: boolean;
  onSave: (patch: Partial<AssessmentPathway>) => Promise<void>;
}

/**
 * StagesSection — four toggles for the stages this cycle will run through:
 * admit card, test, interview, custom marks. Each toggle changes what UI
 * appears on the Candidate drawer + which merit-component sources are
 * available on the scoring config.
 */

type StageKey = 'admitCardEnabled' | 'testEnabled' | 'interviewEnabled' | 'customMarksEnabled';

const STAGES: Array<{ key: StageKey; label: string; sub: string; Icon: React.ElementType }> = [
  { key: 'admitCardEnabled',    label: 'Admit cards',     sub: 'Roll numbers and a printable admit card before the assessment',     Icon: BadgeOutlined },
  { key: 'testEnabled',         label: 'Entrance test',   sub: 'Record marks for each test paper',                            Icon: AssignmentOutlined },
  { key: 'interviewEnabled',    label: 'Interview & observation', sub: 'Interview or observation rounds, each with its own sheet',        Icon: RecordVoiceOverOutlined },
  { key: 'customMarksEnabled',  label: 'Custom scoring',  sub: 'Extra criteria such as Hifz or extracurriculars, added to merit', Icon: StarOutline },
];

const StagesSection: React.FC<Props> = ({ pathway, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(pathway.stages);

  useEffect(() => {
    if (editing) setDraft(pathway.stages);
  }, [editing, pathway.stages]);

  const flip = (k: StageKey) => (_: unknown, v: boolean) =>
    setDraft(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ stages: draft });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const current = editing ? draft : pathway.stages;
  const activeCount = STAGES.filter(s => current[s.key]).length;

  return (
    <SectionCard
      title="Stages"
      description={`The steps applicants go through after applying. ${activeCount} of ${STAGES.length} on.`}
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft(pathway.stages)}
      saving={saving}
    >
      <TableCard>
        {STAGES.map(s => {
          const value = current[s.key];
          const Icon = s.Icon;
          return (
            <ListRow key={s.key}>
              <Icon fontSize="small" sx={{ color: value ? 'text.secondary' : 'text.disabled' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} color={value ? 'text.primary' : 'text.secondary'}>{s.label}</Typography>
                <Typography variant="caption" color="text.secondary">{s.sub}</Typography>
              </Box>
              {editing
                ? <Switch size="small" checked={value} onChange={flip(s.key)} />
                : <OnOffPill on={value} />}
            </ListRow>
          );
        })}
      </TableCard>
    </SectionCard>
  );
};

export default StagesSection;
